import {
  ArrowLeftIcon,
  Badge,
  ExternalIcon,
  formatDate,
  formatMoney,
  HelpIcon,
  IconButton,
} from '@setel/portal-ui';
import { environments } from 'environments/environment';
import { AxiosError } from 'axios';
import { useAdminReadTransaction } from '../topup-and-payment.queries';
import { Link, useLocation } from 'react-router-dom';
import { LoadingPage } from 'modules/pages/loading-page';
import {
  GetBadgeColorForStatus,
  TopupBadgeStatus,
} from '../topup-and-payments.helper';
import MasterCardLogo from 'assets/icons/mastercard.png';
import GrabLogo from 'assets/icons/icon-grabpay.svg';
import OnlineBankingLogo from 'assets/icons/icon-maybank2u.svg';
import AmericanExpressLogo from 'assets/icons/american-express.png';
import BoostLogo from 'assets/icons/boost.png';
import SetelWalletLogo from 'assets/icons/setel-wallet.svg';
import WalletTngLogo from 'assets/icons/wallet_tng.svg';
import WalletShopee from 'assets/icons/wallet-shopee.svg';
import VoucherLogo from 'assets/icons/vouchers.svg';
import VisaLogo from 'assets/icons/visa.png';
import SmartPayLogo from 'assets/icons/smart-pay.png';
import GiftCardLogo from 'assets/icons/gift-card.svg';
import {
  NavigationDropdownSearchQueryKey,
  NavigationDropdownSearchQueryValue,
} from 'components/navigation-dropdown';
import React from 'react';
import { pageview } from 'shared/analytics/gtag';
import {
  DigitalWallet,
  DigitalWalletMap,
  PaymentMethod,
  PaymentSubmethod,
} from 'shared/enums/payments.enum';
import { ITopupPaymentTransaction } from '../../../shared/interfaces/transaction.interface';

type TopupDetailsProps = {
  transactionId: string;
};

export function TopupandPaymentDetails({ transactionId }: TopupDetailsProps) {
  // added ga for url tracking
  let location = useLocation();
  React.useEffect(() => {
    pageview(location.pathname, 'Top Up and Payment');
  }, [location]);

  const {
    data: topupDetails,
    isError: topupDetailsError,
    isLoading: topupDetailsLoading,
  } = useAdminReadTransaction(transactionId, {
    retry: (retryCount, err) => {
      const error = err as AxiosError;
      return retryCount < 3 && error?.response?.status !== 404;
    },
  });

  return (
    <>
      {topupDetailsLoading && <LoadingPage />}
      {topupDetailsError && <TopupDetailsNotFoundPage />}
      {!topupDetails ? (
        <TopupDetailsNotFoundPage />
      ) : (
        <div className="border-gray-200 border">
          <div className="px-4 py-5 flex items-center space-x-4">
            <Link
              className="flex items-center"
              to={`/accounts/${topupDetails?.userId}?${NavigationDropdownSearchQueryKey}=${NavigationDropdownSearchQueryValue.TOPUP_AND_PAYMENTS}`}
            >
              <IconButton className="p-0">
                <ArrowLeftIcon className="w-4 h-4 text-lightgrey" />
              </IconButton>
            </Link>
            <h1 className="text-lg font-medium">Top-up details</h1>
          </div>
          <hr />
          <div className="px-4 py-5">
            <dl className="grid grid-cols-1">
              <DisplayKeyValueInBlock
                label="MERCHANT NAME"
                value={topupDetails.meta?.merchantName || ''}
              />
              <DisplayKeyValueInBlock
                label="STATUS"
                value={
                  <Badge
                    color={
                      topupDetails?.status
                        ? GetBadgeColorForStatus[topupDetails.status]
                        : 'grey'
                    }
                  >
                    {topupDetails?.status
                      ? TopupBadgeStatus[topupDetails.status]
                      : '-'}
                  </Badge>
                }
              />
              <DisplayKeyValueInBlock
                label="AMOUNT"
                value={
                  topupDetails?.amount
                    ? `RM${formatMoney(topupDetails.amount)}`
                    : '-'
                }
              />
              <DisplayKeyValueInBlock
                label="TRANSACTION ID"
                value={topupDetails?.id || '-'}
                link={`${environments.webAdminUrl}/payments/transactions/${topupDetails?.id}`}
              />
              {renderTopUpMethod(topupDetails)}
              <DisplayKeyValueInBlock
                label="ERROR MESSAGE"
                value={
                  topupDetails?.error?.message
                    ? topupDetails.error.message
                    : '-'
                }
              />
              <DisplayKeyValueInBlock
                label="REMARKS"
                value={topupDetails?.message || '-'}
              />
              <DisplayKeyValueInBlock
                label="CREATED ON"
                value={
                  topupDetails?.createdAt
                    ? formatDate(topupDetails.createdAt)
                    : '-'
                }
              />
            </dl>
          </div>
        </div>
      )}
    </>
  );
}

export const renderTopUpMethod = (topupDetails: ITopupPaymentTransaction) => {
  console.log(topupDetails);
  let logoSrc,
    logoAlt,
    methodValueHidden,
    methodName,
    showTopUpMethod = true;
  if (
    topupDetails?.creditCardTransaction ||
    topupDetails?.paymentMethod === PaymentMethod.CARD
  ) {
    // Pay using card
    logoSrc = mappingPaymentMethodLogosUsingCard(
      topupDetails?.creditCardTransaction?.cardSchema ||
        topupDetails?.paymentSubmethod
    );
    logoAlt =
      topupDetails?.creditCardTransaction?.cardSchema ||
      topupDetails?.paymentSubmethod;
    methodValueHidden = '••••';
    methodName =
      topupDetails?.creditCardTransaction?.lastForDigits ||
      topupDetails?.paymentMethodData?.lastFourDigits;
  } else if (topupDetails?.paymentMethod === PaymentMethod.WALLET) {
    // Pay using wallet
    logoSrc = mappingPaymentMethodLogosUsingWallet(
      topupDetails.paymentSubmethod
    );
    logoAlt = topupDetails?.paymentSubmethod;
    methodName = mappingPaymentMethodWalletName(topupDetails?.paymentSubmethod);
  } else if (topupDetails?.paymentMethod === PaymentMethod.ONLINEBANKING) {
    // Pay using banking
    logoSrc = mappingPaymentMethodLogosUsingOtherMethod(
      topupDetails.paymentMethod
    );
    logoAlt = topupDetails?.paymentMethod;
    methodName =
      topupDetails.onlineBankInformation?.issuingBank || 'Online banking';
  } else if (topupDetails?.paymentMethod === PaymentMethod.SMARTPAY) {
    // Pay using vouchers
    logoSrc = mappingPaymentMethodLogosUsingSmartPay(
      topupDetails.paymentMethod
    );
    logoAlt = topupDetails?.paymentMethod;
  }  else if (topupDetails?.paymentMethod === PaymentMethod.VOUCHERS) {
    // Pay using vouchers
    logoSrc = mappingPaymentMethodLogosUsingVouchers(
      topupDetails.paymentMethod
    );
    logoAlt = topupDetails?.paymentMethod;
    methodName = topupDetails?.paymentMethod;
  } else if (topupDetails?.paymentMethod === PaymentMethod.GIFT_CARD) {
    // Pay using gift card
    logoSrc = mappingPaymentMethodLogosUsingGiftCard(
      topupDetails.paymentMethod
    );
    logoAlt = topupDetails?.paymentMethod;
    methodName = 'Gift card';
  } else if (
    topupDetails?.paymentMethod === undefined &&
    topupDetails?.paymentSubmethod === undefined
  ) {
    logoSrc = mappingPaymentMethodLogosUsingWallet(
      PaymentSubmethod.WALLET_SETEL
    );
    logoAlt = PaymentSubmethod.WALLET_SETEL;
    methodName = mappingPaymentMethodWalletName(PaymentSubmethod.WALLET_SETEL);
  } else if (topupDetails?.subtype) {
    logoSrc = mappingPaymentMethodLogosUsingOtherMethod(
      topupDetails.paymentMethod
    );
    logoAlt = topupDetails?.paymentMethod;
    methodName = CustomerPaymentMethodType(topupDetails.subtype);
  }

  return (
    showTopUpMethod && (
      <div>
        <DisplayKeyValueInBlock
          label="PAYMENT METHOD"
          value={
            <div className="h-9 flex items-center">
              <img
                src={logoSrc}
                alt={logoAlt}
                className="inline w-12 h-8 mr-2"
              />
              <div className="flex flex-row h-5" style={{ lineHeight: '22px' }}>
                {methodValueHidden && (
                  <p className="left-2 bottom-0 leading-4 tracking-widest pl-2">
                    {methodValueHidden}
                  </p>
                )}
                <p className="font-normal">{methodName}</p>
                {topupDetails?.paymentSubmethod ===
                  PaymentSubmethod.WALLET_SETEL && (
                  <>
                    <p className="px-2 font-extrabold" style={{ lineHeight: '12px' }}>.</p>
                    <p className="font-normal">
                      {topupDetails?.walletBalance
                        ? `RM${formatMoney(topupDetails.walletBalance)}`
                        : '-'}
                    </p>
                  </>
                )}
              </div>
            </div>
          }
        />
      </div>
    )
  );
};

export function TopupDetailsNotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <HelpIcon className="text-gray-500 h-9 w-9 mb-3.5" />
      <div className="text-center leading-5 mb-5">
        <p className="text-gray-500">Unable to find topup details.</p>
        <p className="text-gray-500">Information is incomplete.</p>
      </div>
      <a
        target="_blank"
        href={`${environments.webAdminUrl}`}
        className="text-brand-500 tracking-1 font-semibold"
        rel="noopener noreferrer"
      >
        <span className="text-xs">OPEN PORTAL</span>
        <ExternalIcon className="inline ml-2" />
      </a>
    </div>
  );
}

interface DisplayKeyValueInBlockProps {
  label: string;
  value: JSX.Element | string;
  link?: string;
}

function DisplayKeyValueInBlock({
  label,
  value,
  link,
}: DisplayKeyValueInBlockProps) {
  return (
    <div>
      <div className="block tracking-1 leading-4 text-lightgrey mb-1 text-xs font-bold">
        {label}
      </div>
      <div className="text-base mb-5">
        {value}
        {link && (
          <a
            href={link}
            className="text-brand-500 tracking-1 font-semibold"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalIcon
              className="ml-1 inline"
              style={{ width: 13, height: 13 }}
            />
          </a>
        )}
      </div>
    </div>
  );
}

const mappingPaymentMethodWalletName = (topupMethod: string | undefined) => {
  if (Object.values(DigitalWallet).includes(topupMethod as DigitalWallet)) {
    return DigitalWalletMap.get(topupMethod as DigitalWallet);
  }
  return '-';
};

const mappingPaymentMethodLogosUsingCard = (
  topupMethod: string | undefined
) => {
  switch (topupMethod) {
    case 'card_mastercard':
      return MasterCardLogo;
    case 'mc':
      return MasterCardLogo;
    case 'card_amex':
      return AmericanExpressLogo;
    case 'visa':
      return VisaLogo;
    case 'card_visa':
      return VisaLogo;
    case 'online_banking':
      return VisaLogo;
    default:
      return '-';
  }
};

const mappingPaymentMethodLogosUsingOtherMethod = (
  topupMethod: string | undefined
) => {
  switch (topupMethod) {
    case 'online_banking':
      return OnlineBankingLogo;
    default:
      return '-';
  }
};

const mappingPaymentMethodLogosUsingVouchers = (
  topupMethod: string | undefined
) => {
  switch (topupMethod) {
    case 'vouchers':
      return VoucherLogo;
    default:
      return '-';
  }
};

const mappingPaymentMethodLogosUsingSmartPay = (
  topupMethod: string | undefined
) => {
  switch (topupMethod) {
    case 'smartpay':
      return SmartPayLogo;
    default:
      return '-';
  }
};

const mappingPaymentMethodLogosUsingGiftCard = (
  topupMethod: string | undefined
) => {
  switch (topupMethod) {
    case 'gift_card':
      return GiftCardLogo;
    default:
      return '-';
  }
};

const mappingPaymentMethodLogosUsingWallet = (
  topupMethod: string | undefined
) => {
  switch (topupMethod) {
    case 'wallet_setel':
      return SetelWalletLogo;
    case 'wallet_boost':
      return BoostLogo;
    case 'wallet_grab':
      return GrabLogo;
    case 'wallet_shopeepay':
      return WalletShopee;
    case 'wallet_tng':
      return WalletTngLogo;
    default:
      return '-';
  }
};

const CustomerPaymentMethodType = (otherTopupMethod: string | undefined) => {
  switch (otherTopupMethod) {
    case 'TOPUP_EXTERNAL':
      return 'External top-up';
    case 'REFUND_EXTERNAL':
      return 'External top-up refund';
    case 'TOPUP_BANK_ACCOUNT':
      return 'Bank';
    case 'REDEEM_LOYALTY_POINTS':
      return 'Mesra Point Redemption';
    case 'REWARDS':
      return 'Granted Wallet Balance';
    case 'AUTO_TOPUP':
      return 'Auto Top-up';
    default:
      return '-';
  }
};
